import { GripVertical, X, ChevronLeft, ChevronRight, Wand2, Crop, AlertTriangle, Monitor, HardDrive, Cloud, RefreshCw } from "lucide-react";
import { presetLabel } from "@/lib/presetClassify";

const SOURCE = {
  computer: { icon: Monitor, label: "Computer" },
  drive: { icon: HardDrive, label: "Google Drive" },
  dropbox: { icon: Cloud, label: "Dropbox" },
};

function dupKeys(photos) {
  const seen = {};
  const dups = new Set();
  photos.forEach((p) => {
    const k = `${p.filename}|${p.size}`;
    if (seen[k]) dups.add(p.imageId);
    else seen[k] = true;
  });
  return dups;
}

export function PhotoGrid({ photos, presets, selectedIds = [], variant = "order", onOpenEditor, onRemove, onMove, onReorder, onToggleSelect, onReplace }) {
  const dups = dupKeys(photos);
  const selectable = variant === "effects" || variant === "crop";

  const onDragStart = (e, i) => e.dataTransfer.setData("idx", String(i));
  const onDrop = (e, i) => {
    const from = parseInt(e.dataTransfer.getData("idx"), 10);
    if (!Number.isNaN(from) && from !== i && onReorder) {
      const next = [...photos];
      const [it] = next.splice(from, 1);
      next.splice(i, 0, it);
      onReorder(next);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="photo-grid">
      {photos.map((p, i) => {
        const src = SOURCE[p.source] || SOURCE.computer;
        const SrcIcon = src.icon;
        const isDup = dups.has(p.imageId);
        const isSel = selectedIds.includes(p.imageId);
        return (
          <div
            key={p.imageId}
            draggable={variant === "order"}
            onDragStart={(e) => onDragStart(e, i)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => onDrop(e, i)}
            data-testid={`grid-card-${i}`}
            className={`group relative overflow-hidden rounded-2xl border bg-white transition-all ${isSel ? "border-re-red ring-2 ring-re-red/30" : "border-re-line"}`}
          >
            <div className="relative aspect-video w-full bg-re-soft">
              <img src={p.previewUrl} alt={`Foto ${i + 1}`} className="h-full w-full object-cover" />
              <span className="absolute left-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-black/70 text-xs font-bold text-white" data-testid={`grid-seq-${i}`}>{i + 1}</span>
              {variant === "order" && (
                <span className="absolute right-2 top-2 hidden cursor-grab rounded-md bg-white/90 p-1 text-re-muted group-hover:block">
                  <GripVertical className="h-4 w-4" />
                </span>
              )}
              {selectable && (
                <button type="button" onClick={() => onToggleSelect(p.imageId)} data-testid={`grid-select-${i}`} className={`absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-md border-2 ${isSel ? "border-re-red bg-re-red text-white" : "border-white bg-white/70"}`}>
                  {isSel ? "✓" : ""}
                </button>
              )}
            </div>
            <div className="p-3">
              <div className="flex items-center gap-1.5 text-xs text-re-muted">
                <SrcIcon className="h-3.5 w-3.5" /> {src.label}
              </div>
              <p className="mt-1 truncate text-sm font-medium text-re-charcoal">{p.filename}</p>
              {isDup && (
                <p className="mt-1 inline-flex items-center gap-1 text-[11px] text-amber-600"><AlertTriangle className="h-3 w-3" /> Mogelijk duplicaat</p>
              )}
              <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
                <span className="inline-flex items-center gap-1 rounded-full bg-re-soft px-2 py-0.5 text-re-muted"><Wand2 className="h-3 w-3" /> {presetLabel(p.presetKey, presets)}</span>
                <span className="inline-flex items-center gap-1 rounded-full bg-re-soft px-2 py-0.5 text-re-muted"><Crop className="h-3 w-3" /> {p.crop ? "Aangepast" : "Automatisch"}</span>
              </div>
              <div className="mt-3 flex items-center justify-between">
                <div className="flex gap-1">
                  {variant === "order" && (
                    <>
                      <button type="button" disabled={i === 0} onClick={() => onMove(i, i - 1)} data-testid={`grid-left-${i}`} className="rounded-md border border-re-line p-1 text-re-muted disabled:opacity-30"><ChevronLeft className="h-3.5 w-3.5" /></button>
                      <button type="button" disabled={i === photos.length - 1} onClick={() => onMove(i, i + 1)} data-testid={`grid-right-${i}`} className="rounded-md border border-re-line p-1 text-re-muted disabled:opacity-30"><ChevronRight className="h-3.5 w-3.5" /></button>
                    </>
                  )}
                  {(variant === "effects" || variant === "crop") && (
                    <button type="button" onClick={() => onOpenEditor(i, variant === "crop" ? "crop" : "effects")} data-testid={`grid-edit-${i}`} className="rounded-full bg-re-red/10 px-3 py-1 text-xs font-medium text-re-red">
                      {variant === "crop" ? "Uitsnede bewerken" : "Beweging & effect"}
                    </button>
                  )}
                  {variant === "order" && onReplace && (
                    <button type="button" onClick={() => onReplace(p.imageId)} data-testid={`grid-replace-${i}`} className="rounded-md border border-re-line p-1 text-re-muted" title="Vervangen"><RefreshCw className="h-3.5 w-3.5" /></button>
                  )}
                </div>
                <button type="button" onClick={() => onRemove(p.imageId)} data-testid={`grid-remove-${i}`} className="rounded-md p-1 text-re-muted hover:text-re-red"><X className="h-4 w-4" /></button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
